import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Tax Year Selection Store
 *
 * Manages the operator-selected tax year for the Tax Reporting section.
 * - Persists to localStorage so the selection survives page reloads.
 * - Defaults to the prior calendar year (the year typically being filed).
 *   This is data-agnostic by design — the UI must allow selecting a year
 *   that has no data yet so the operator can kick off a new pipeline run
 *   from CTR-014.
 * - yearChangeCounter increments on every change so view components can
 *   subscribe and refetch their queries.
 *
 * This is the first Zustand store in the codebase using localStorage; the
 * existing useGlobalAutoRefreshStore uses sessionStorage and useDateRangeStore
 * is in-memory.
 */

/**
 * The earliest tax year the picker offers. The platform's tax pipeline
 * began processing data in 2024; older years are out of scope until
 * CTR-009 (Multi-Year Support) introduces carry-forward.
 */
export const TAX_YEAR_FLOOR = 2024;

/** Returns the prior calendar year — the typical default for tax filing. */
export const defaultTaxYear = (): number => new Date().getFullYear() - 1;

interface TaxYearState {
  selectedYear: number;
  yearChangeCounter: number;
  setSelectedYear: (year: number) => void;
  bumpYearChangeCounter: () => void;
}

export const useTaxYearStore = create<TaxYearState>()(
  persist(
    (set) => ({
      selectedYear: defaultTaxYear(),
      yearChangeCounter: 0,

      setSelectedYear: (year: number) =>
        set((state) => ({
          selectedYear: year,
          yearChangeCounter: state.yearChangeCounter + 1,
        })),

      bumpYearChangeCounter: () =>
        set((state) => ({
          yearChangeCounter: state.yearChangeCounter + 1,
        })),
    }),
    {
      name: 'tax-year-selector',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ selectedYear: state.selectedYear }),
    }
  )
);

/**
 * Hook returning the currently selected tax year.
 */
export const useTaxYear = (): number => useTaxYearStore((state) => state.selectedYear);

/**
 * Hook returning the year change counter for reactive refetch.
 */
export const useTaxYearChangeCounter = (): number =>
  useTaxYearStore((state) => state.yearChangeCounter);
